import React, { useState, useEffect, useCallback } from 'react';
import { 
    Trash2, Search, 
    Plus, 
    X, 
    AlertCircle, Loader2, CheckCircle2, Zap, CheckCircle
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';

const EnregistrerCommande = () => {
    const [fichesTechniques, setFichesTechniques] = useState([]);
    const [produitsFinis, setProduitsFinis] = useState([]);
    const [produitsSemiFinis, setProduitsSemiFinis] = useState([]);
    const [historiqueValidé, setHistoriqueValidé] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFT, setSelectedFT] = useState(null);
    const [quantite, setQuantite] = useState(1); // Représente le NOMBRE DE LOTS
    const [panierCommandes, setPanierCommandes] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [numeroCommande] = useState(`PROD-${Date.now().toString().slice(-4)}`);
    
    const [notification, setNotification] = useState({
        isOpen: false,
        type: 'success',
        message: '',
        onConfirm: null
    });

    const borderColor = '#e2e8f0';

    // --- CHARGEMENT INITIAL DES DONNÉES ET DE L'HISTORIQUE DEPUIS LE BACKEND ---
    const fetchInitialData = useCallback(async () => {
        try {
            const [ftRes, prodRes, psfRes, histRes] = await Promise.all([
                API.get('/technical-sheets').catch(() => ({ data: [] })),
                API.get('/products').catch(() => ({ data: [] })),
                API.get('/semi-finished-products').catch(() => ({ data: [] })),
                API.get('/production-needs/historique').catch(() => ({ data: [] }))
            ]);

            const fichesEligibles = (ftRes.data || []).map(ft => ({
                ...ft,
                unite_affichage: (ft.unite_mesure || ft.unite || 'UNITE').toUpperCase(),
                quantite_base: Number(ft.quantite_base) > 0 ? Number(ft.quantite_base) : 1
            })).filter(ft => ft.is_active === 1 && (ft.semi_fini_id || ft.product_id));
            
            setFichesTechniques(fichesEligibles);
            setProduitsFinis(Array.isArray(prodRes.data) ? prodRes.data.filter(p => p.is_active === 1) : []);
            setProduitsSemiFinis(Array.isArray(psfRes.data) ? psfRes.data.filter(p => p.is_active === 1) : []);
            
            const historiqueFormate = (histRes.data || []).map(h => ({
                id: h.id,
                article_nom: h.produit_nom || h.psf_nom || h.article_nom || 'Article produit',
                quantite: h.quantite_produite,
                unite: h.unite || 'UNITE',
                statut_ligne: h.statut || 'TERMINEE',
                observation_magasin: h.observation || 'Production directe validée'
            }));
            setHistoriqueValidé(historiqueFormate);
        } catch (err) { 
            showToast("Erreur de chargement des données", "error");
        }
    }, []);

    const getNomAffichage = (ft) => {
        if (ft.nom && ft.nom.trim() !== '' && ft.nom.toLowerCase() !== 'non lié') {
            return ft.nom;
        }
        if (ft.product_id) {
            const p = produitsFinis.find(item => item.id === ft.product_id);
            return p ? p.nom : (ft.nom_produit || 'Produit Fini');
        }
        if (ft.semi_fini_id) {
            const s = produitsSemiFinis.find(item => item.id === ft.semi_fini_id);
            return s ? s.nom : (ft.nom_produit || 'Produit Semi-Fini');
        }
        return ft.nom_produit || 'Article sans nom';
    };

    const getTypeArticleLabel = (ft) => {
        if (ft.product_id && !ft.semi_fini_id) return { text: 'PRODUIT FINI (PF)', color: '#059669' };
        if (ft.semi_fini_id && !ft.product_id) return { text: 'PRODUIT SEMI-FINI (PSF)', color: '#d97706' };
        return { text: 'ARTICLE', color: '#7c3aed' };
    };

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    useEffect(() => {
        if (notification.isOpen && notification.type !== 'confirm') {
            const timer = setTimeout(() => setNotification(prev => ({ ...prev, isOpen: false })), 4000);
            return () => clearTimeout(timer);
        }
    }, [notification.isOpen, notification.type]);

    const showToast = (message, type = 'success') => {
        setNotification({ isOpen: true, type, message, onConfirm: null });
    };

    const ajouterAuPanier = () => {
        const nbLots = Math.round(parseFloat(quantite) * 1000) / 1000;
        if (!selectedFT) return showToast("Sélectionnez un article", "error");
        if (isNaN(nbLots) || nbLots <= 0) return showToast("Nombre de lots invalide", "error");
        
        // Volume total affiché dans le panier à titre indicatif = Nombre de lots x Quantité de base
        const qteTotaleEstimee = nbLots * selectedFT.quantite_base;
        
        const uniteADonner = selectedFT.unite_affichage || 'UNITE';
        const nomFinal = getNomAffichage(selectedFT);
        const typeInfo = getTypeArticleLabel(selectedFT);

        setPanierCommandes([...panierCommandes, { 
            id: `CMD-${Date.now().toString().slice(-6)}`, 
            ft_id: selectedFT.id, 
            semi_fini_id: selectedFT.semi_fini_id,
            product_id: selectedFT.product_id,
            nom: nomFinal, 
            typeLabel: typeInfo.text,
            nombre_lots: nbLots, // CORRECTION : On stocke le nombre de lots pur (ex: 1)
            qteEstimee: qteTotaleEstimee, 
            quantite_base: selectedFT.quantite_base,
            unite: uniteADonner 
        }]);
        
        setSelectedFT(null);
        setQuantite(1);
        showToast(`Ajouté au panier : ${nbLots} lot(s) -> ${qteTotaleEstimee} ${uniteADonner}`);
    };

    const executerValidationGlobale1Clic = async () => {
        if (panierCommandes.length === 0) return showToast("Le panier est vide", "error");
        setIsSubmitting(true);
        try {
            const promesses = panierCommandes.map(item => 
                API.post('/production-rapide/directe', {
                    ft_id: item.ft_id,
                    quantite_produite: item.nombre_lots // CORRECTION : Envoi du NOMBRE DE LOTS (ex: 1) au backend
                })
            );
            const resultats = await Promise.all(promesses);
            showToast("Production directe globale validée ! Mouvements de stock, CMP et consommations enregistrés.");
            
            const nouveauxElements = resultats.map((res, index) => ({
                id: res.data?.data?.productionId || panierCommandes[index].id,
                article_nom: panierCommandes[index].nom,
                quantite: panierCommandes[index].qteEstimee,
                unite: panierCommandes[index].unite,
                statut_ligne: 'TERMINEE',
                observation_magasin: `Validé en production directe (1 clic) - Lots: ${panierCommandes[index].nombre_lots}`
            }));
            setHistoriqueValidé(prev => [...nouveauxElements, ...prev]);
            setPanierCommandes([]);
        } catch (err) {
            const msg = err.response?.data?.error || "Erreur lors de la validation de la production globale";
            showToast(msg, "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const s = {
        page: { display: 'flex', background: '#f8fafc', height: '100vh', width: '100%', overflow: 'hidden', position: 'relative' },
        main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
        header: { background: '#fff', padding: '15px 30px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        content: { flex: 1, padding: '25px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '25px' },
        gridForm: { display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '20px', minHeight: '420px' },
        table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
        th: { textAlign: 'left', padding: '12px 15px', fontSize: '11px', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase' },
        td: { padding: '12px 15px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', color: '#1e293b' },
        toastContainer: {
            position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)',
            zIndex: 1000, display: 'flex', alignItems: 'center',
            background: 'white', padding: '12px 20px', borderRadius: '15px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            border: `1px solid ${borderColor}`,
            minWidth: '400px',
            transition: 'all 0.3s ease'
        }
    };

    const NotificationToast = () => {
        if (!notification.isOpen) return null;
        const color = notification.type === 'error' ? '#ef4444' : '#10b981';
        return (
            <div style={{ ...s.toastContainer, borderLeft: `8px solid ${color}` }}>
                <div style={{ 
                    display: 'flex', justifyContent: 'center', alignItems: 'center', 
                    width: '35px', height: '35px', borderRadius: '50%', 
                    background: `${color}15`, marginRight: '15px', flexShrink: 0
                }}>
                    {notification.type === 'error' ? <AlertCircle size={20} color={color} /> : <CheckCircle2 size={20} color={color} />}
                </div>
                <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>{notification.message}</p>
                </div>
                <button onClick={() => setNotification({isOpen: false})} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1' }}>
                    <X size={20} />
                </button>
            </div>
        );
    };

    return (
        <div style={s.page}>
            <Sidebar />
            <div style={s.main}>
                <NotificationToast />
                <header style={s.header}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ background: '#f3e8ff', padding: '10px', borderRadius: '10px' }}>
                            <Zap size={28} color="#7c3aed" />
                        </div>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '900' }}>PRODUCTION DIRECTE & ENREGISTREMENT</h2>
                            <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Validation globale et immédiate en 1 clic sur l'ensemble des circuits et stocks</p>
                        </div>
                    </div>
                </header>

                <div style={s.content}>
                    <div style={s.gridForm}>
                        <div style={{ background: '#fff', padding: '20px', borderRadius: '15px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ position: 'relative', marginBottom: '15px' }}>
                                <Search size={18} style={{ position: 'absolute', left: '12px', top: '12px', color: '#94a3b8' }} />
                                <input style={{ width: '100%', padding: '10px 40px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }} placeholder="Chercher un article..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                            </div>
                            <div style={{ flex: 1, overflowY: 'auto', maxHeight: '280px' }}>
                                {fichesTechniques
                                    .filter(ft => getNomAffichage(ft).toLowerCase().includes(searchTerm.toLowerCase()))
                                    .map(ft => {
                                        const nomAffichage = getNomAffichage(ft);
                                        const typeInfo = getTypeArticleLabel(ft);
                                        return (
                                            <div key={ft.id} onClick={() => setSelectedFT(ft)} style={{ padding: '12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: selectedFT?.id === ft.id ? '#f3e8ff' : 'transparent', borderRadius: '8px' }}>
                                                <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{nomAffichage}</div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                                                    <span style={{ fontSize: '10px', fontWeight: 'bold', color: typeInfo.color }}>{typeInfo.text} (Base: {ft.quantite_base})</span>
                                                    <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#7c3aed' }}>Unité : {ft.unite_affichage}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>

                            {selectedFT && (
                                <div style={{ marginTop: '15px', background: '#1e293b', padding: '15px', borderRadius: '12px', color: '#fff' }}>
                                    <div style={{ fontSize: '11px', marginBottom: '8px', fontWeight: 'bold', color: '#c084fc', display: 'flex', justifyContent: 'space-between' }}>
                                        <span>NOMBRE DE LOTS À PRODUIRE :</span>
                                        <span style={{ color: '#fbbf24' }}>Base FT : {selectedFT.quantite_base} {selectedFT.unite_affichage}</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <input 
                                            type="number" 
                                            step="any"
                                            min="0.0001"
                                            value={quantite} 
                                            onChange={(e) => setQuantite(e.target.value)} 
                                            style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', color: '#000', fontWeight: 'bold', outline: 'none', fontSize: '15px' }} 
                                        />
                                        <div style={{ background: '#334155', padding: '10px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold', color: '#34d399', whiteSpace: 'nowrap' }}>
                                            = {(parseFloat(quantite || 0) * selectedFT.quantite_base)} {selectedFT.unite_affichage}
                                        </div>
                                        <button onClick={ajouterAuPanier} style={{ background: '#7c3aed', color: '#fff', padding: '10px 15px', borderRadius: '8px', border: 'none', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                                            <Plus size={16} /> AJOUTER
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div style={{ background: '#fff', padding: '20px', borderRadius: '15px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ fontWeight: '900', marginBottom: '15px', color: '#1e293b' }}>PANIER DE PRODUCTION ({numeroCommande})</div>
                            <div style={{ flex: 1, overflowY: 'auto', maxHeight: '300px' }}>
                                {panierCommandes.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>Panier vide. Ajoutez des articles à produire.</div>
                                ) : panierCommandes.map((item, idx) => (
                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                        <div>
                                            <div style={{fontWeight: 'bold', fontSize: '13px'}}>{item.nom}</div>
                                            <div style={{fontSize: '10px', fontWeight: 'bold', color: item.product_id ? '#059669' : '#d97706'}}>{item.typeLabel}</div>
                                            <div style={{fontSize: '11px', color: '#64748b'}}>Lots : {item.nombre_lots} (Base: {item.quantite_base})</div>
                                            <div style={{fontSize: '13px', color: '#7c3aed', fontWeight: '900'}}>Volume Total : {item.qteEstimee} {item.unite}</div>
                                        </div>
                                        <button onClick={() => setPanierCommandes(panierCommandes.filter((_, i) => i !== idx))} style={{background: '#fee2e2', border: 'none', padding: '8px', borderRadius: '6px', cursor: 'pointer'}}><Trash2 size={16} color="#dc2626" /></button>
                                    </div>
                                ))}
                            </div>
                            <button 
                                onClick={executerValidationGlobale1Clic} 
                                disabled={panierCommandes.length === 0 || isSubmitting} 
                                style={{ width: '100%', background: '#7c3aed', color: '#fff', border: 'none', padding: '15px', borderRadius: '12px', fontWeight: '900', cursor: 'pointer', marginTop: '15px', opacity: (panierCommandes.length === 0 || isSubmitting) ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                            >
                                {isSubmitting ? <><Loader2 size={20} className="animate-spin" /> VALIDATION EN COURS...</> : <><Zap size={20} /> VALIDER TOUTE LA PRODUCTION (1 CLIC)</>}
                            </button>
                        </div>
                    </div>

                    <div style={{ background: '#fff', padding: '20px', borderRadius: '15px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontWeight: '900', marginBottom: '15px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <CheckCircle size={18} color="#059669" /> HISTORIQUE DES MOUVEMENTS DE PRODUCTION ENREGISTRÉS
                        </div>
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    <th style={s.th}>ID Production</th>
                                    <th style={s.th}>Article Produit</th>
                                    <th style={s.th}>Quantité</th>
                                    <th style={s.th}>Statut Mouvement</th>
                                    <th style={s.th}>Observation</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historiqueValidé.length > 0 ? historiqueValidé.map((h, i) => (
                                    <tr key={i}>
                                        <td style={s.td}><span style={{ fontWeight: 'bold', color: '#2563eb' }}>{h.id}</span></td>
                                        <td style={s.td}><span style={{ fontWeight: '900' }}>{h.article_nom}</span></td>
                                        <td style={s.td}><span style={{ fontWeight: 'bold', background: '#f1f5f9', padding: '4px 8px', borderRadius: '6px' }}>{h.quantite} {h.unite}</span></td>
                                        <td style={s.td}>
                                            <span style={{ background: '#dcfce7', color: '#15803d', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold' }}>
                                                {h.statut_ligne}
                                            </span>
                                        </td>
                                        <td style={{ ...s.td, fontSize: '11px', fontStyle: 'italic', color: '#64748b' }}>{h.observation_magasin}</td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="5" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>Aucun mouvement enregistré pour l'instant.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnregistrerCommande;